import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  IconBooks,
  IconPlayerPlay,
  IconListCheck,
  IconFileText,
  IconChartBar,
  IconArrowRight,
  IconBookmark,
  IconSettings,
} from '@tabler/icons-react';
import { LayoutContainer } from '@/components/ui/layout';
import {
  Stack,
  Card,
  Group,
  Button,
  Text,
  Grid,
  Badge,
  Title,
  ThemeIcon,
  Skeleton,
} from '@/components/ui';
import { FadeIn } from '@/components/animations';
import { BarChartWidget, PieChartWidget } from '@/components/advanced/Widgets/charts';
import {
  useCourseService,
  useCourseSessionService,
  useCoursePrerequisiteService,
  useKBArticleService,
  useKnowledgeBaseService,
  useCourseStatusService,
  useCourseSessionTypeService,
  useKBArticleStatusService,
} from '@actschurch/ccms-sdk';

const ACCENT = '#16A085';

export default function ResourcesDashboard() {
  const courseService = useCourseService();
  const sessionService = useCourseSessionService();
  const prereqService = useCoursePrerequisiteService();
  const kbArticleService = useKBArticleService();
  const kbService = useKnowledgeBaseService();
  const courseStatusService = useCourseStatusService();
  const sessionTypeService = useCourseSessionTypeService();
  const articleStatusService = useKBArticleStatusService();

  const { data: courses = [], isLoading: loadingCourses } = useQuery({
    queryKey: ['resources', 'courses', 'all'],
    queryFn: () => courseService.getAll(),
  });
  const { data: sessions = [], isLoading: loadingSessions } = useQuery({
    queryKey: ['resources', 'course-sessions', 'all'],
    queryFn: () => sessionService.getAll(),
  });
  const { data: prereqs = [], isLoading: loadingPrereqs } = useQuery({
    queryKey: ['resources', 'prerequisites', 'all'],
    queryFn: () => prereqService.getAll(),
  });
  const { data: articles = [], isLoading: loadingKb } = useQuery({
    queryKey: ['knowledgebase', 'articles', 'all'],
    queryFn: () => kbArticleService.getAll(),
  });
  const { data: kbs = [] } = useQuery({
    queryKey: ['knowledgebase', 'all'],
    queryFn: () => kbService.getAll(),
  });
  const { data: courseStatuses = [] } = useQuery({
    queryKey: ['resources', 'course-statuses', 'all'],
    queryFn: () => courseStatusService.getAll(),
  });
  const { data: sessionTypes = [] } = useQuery({
    queryKey: ['resources', 'course-session-types', 'all'],
    queryFn: () => sessionTypeService.getAll(),
  });
  const { data: articleStatuses = [] } = useQuery({
    queryKey: ['knowledgebase', 'article-statuses', 'all'],
    queryFn: () => articleStatusService.getAll(),
  });

  const stats = [
    { label: 'Courses', value: courses.length, loading: loadingCourses, Icon: IconBooks, color: 'blue' as const, to: '/app/resources/courses' },
    { label: 'Sessions', value: sessions.length, loading: loadingSessions, Icon: IconPlayerPlay, color: 'grape' as const, to: '/app/resources/sessions' },
    { label: 'Prerequisites', value: prereqs.length, loading: loadingPrereqs, Icon: IconListCheck, color: 'orange' as const, to: '/app/resources/prerequisites' },
    { label: 'KB Articles', value: articles.length, loading: loadingKb, Icon: IconFileText, color: 'teal' as const, to: '/app/resources/knowledge-base' },
  ];

  const courseStatusData = useMemo(() => {
    const counts = new Map<number, number>();
    courses.forEach((c) => counts.set(c.statusID, (counts.get(c.statusID) ?? 0) + 1));
    return courseStatuses
      .map((s) => ({
        name: s.name ?? `#${s.statusID}`,
        value: counts.get(s.statusID) ?? 0,
        color: s.color ?? ACCENT,
      }))
      .filter((d) => d.value > 0);
  }, [courses, courseStatuses]);

  const sessionTypeData = useMemo(() => {
    const counts = new Map<number, number>();
    sessions.forEach((s) => counts.set(s.typeID, (counts.get(s.typeID) ?? 0) + 1));
    return sessionTypes
      .map((t) => ({
        category: t.name ?? `#${t.typeID}`,
        sessions: counts.get(t.typeID) ?? 0,
      }))
      .filter((d) => d.sessions > 0);
  }, [sessions, sessionTypes]);

  const articleStatusData = useMemo(() => {
    const counts = new Map<number, number>();
    articles.forEach((a) => counts.set(a.statusID, (counts.get(a.statusID) ?? 0) + 1));
    return articleStatuses
      .map((s) => ({
        name: s.name ?? `#${s.statusID}`,
        value: counts.get(s.statusID) ?? 0,
        color: s.color ?? ACCENT,
      }))
      .filter((d) => d.value > 0);
  }, [articles, articleStatuses]);

  const quickLinks = [
    { label: 'Manage courses', to: '/app/resources/courses', Icon: IconBooks, desc: 'Create, publish, and edit courses.' },
    { label: 'Knowledge base', to: '/app/resources/knowledge-base', Icon: IconBookmark, desc: 'Articles + step-by-step content.' },
    { label: 'Analytics', to: '/app/resources/analytics', Icon: IconChartBar, desc: 'Engagement charts per course / ministry.' },
    { label: 'Lookups', to: '/app/resources/lookups', Icon: IconSettings, desc: 'Edit visibility, statuses, types — no deploy.' },
  ];

  return (
    <LayoutContainer>
      <FadeIn>
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <Group gap="sm">
              <ThemeIcon size="xl" radius="md" variant="light" color="blue">
                <IconBooks size={24} />
              </ThemeIcon>
              <div>
                <Title order={2}>Resource Centre</Title>
                <Text size="sm" c="dimmed">
                  Courses, sessions, knowledge base and analytics for every ministry.
                </Text>
              </div>
            </Group>
            <Badge size="lg" variant="light" color="teal">
              {kbs.length} knowledge bases
            </Badge>
          </Group>

          <Grid>
            {stats.map((s) => (
              <Grid.Col key={s.label} span={{ base: 6, md: 3 }}>
                <Link to={s.to} style={{ textDecoration: 'none' }}>
                  <Card withBorder padding="lg" radius="md" style={{ cursor: 'pointer' }}>
                    <Stack gap="xs">
                      <Group justify="space-between">
                        <ThemeIcon size="lg" radius="md" variant="light" color={s.color}>
                          <s.Icon size={20} />
                        </ThemeIcon>
                        <Badge color={s.color} variant="light">{s.label}</Badge>
                      </Group>
                      {s.loading ? (
                        <Skeleton height={28} width={80} />
                      ) : (
                        <Text size="xl" fw={700}>
                          {s.value.toLocaleString()}
                        </Text>
                      )}
                      <Text size="xs" c="dimmed">Across all ministries</Text>
                    </Stack>
                  </Card>
                </Link>
              </Grid.Col>
            ))}
          </Grid>

          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              {courseStatusData.length > 0 ? (
                <PieChartWidget
                  title="Course pipeline"
                  subtitle="Where each course stands today"
                  data={courseStatusData}
                  variant="donut"
                  showLegend
                  showExport={false}
                />
              ) : (
                <Card withBorder padding="lg" radius="md" h={300}>
                  <Stack align="center" justify="center" h="100%" gap="xs">
                    <IconBooks size={32} stroke={1.4} opacity={0.5} />
                    <Text size="sm" c="dimmed">No courses yet — create the first one.</Text>
                  </Stack>
                </Card>
              )}
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              {sessionTypeData.length > 0 ? (
                <BarChartWidget
                  title="Sessions by type"
                  subtitle="Video, audio, reading material, PDF, …"
                  data={sessionTypeData}
                  xAxisKey="category"
                  bars={[{ dataKey: 'sessions', name: 'Sessions', color: ACCENT }]}
                  showLegend={false}
                  showExport={false}
                />
              ) : (
                <Card withBorder padding="lg" radius="md" h={300}>
                  <Stack align="center" justify="center" h="100%" gap="xs">
                    <IconPlayerPlay size={32} stroke={1.4} opacity={0.5} />
                    <Text size="sm" c="dimmed">No sessions yet.</Text>
                  </Stack>
                </Card>
              )}
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              {articleStatusData.length > 0 ? (
                <PieChartWidget
                  title="KB article pipeline"
                  subtitle="Draft → Review → Published"
                  data={articleStatusData}
                  variant="donut"
                  showLegend
                  showExport={false}
                />
              ) : (
                <Card withBorder padding="lg" radius="md" h={300}>
                  <Stack align="center" justify="center" h="100%" gap="xs">
                    <IconFileText size={32} stroke={1.4} opacity={0.5} />
                    <Text size="sm" c="dimmed">No KB articles yet — start with a knowledge base.</Text>
                  </Stack>
                </Card>
              )}
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Card withBorder padding="lg" radius="md" h="100%">
                <Title order={5} mb="sm">Quick actions</Title>
                <Stack gap="xs">
                  {quickLinks.map((q) => (
                    <Link key={q.to} to={q.to} style={{ textDecoration: 'none' }}>
                      <Card withBorder padding="sm" radius="sm" style={{ cursor: 'pointer' }}>
                        <Group justify="space-between" wrap="nowrap">
                          <Group gap="sm" wrap="nowrap">
                            <ThemeIcon size="md" variant="light" color="teal">
                              <q.Icon size={16} />
                            </ThemeIcon>
                            <Stack gap={0}>
                              <Text fw={500} size="sm">{q.label}</Text>
                              <Text size="xs" c="dimmed">{q.desc}</Text>
                            </Stack>
                          </Group>
                          <IconArrowRight size={16} />
                        </Group>
                      </Card>
                    </Link>
                  ))}
                </Stack>
              </Card>
            </Grid.Col>
          </Grid>

          <Card withBorder padding="lg" radius="md">
            <Group gap="xs" mb="sm">
              <ThemeIcon size="md" variant="light" color="orange">
                <IconChartBar size={16} />
              </ThemeIcon>
              <Title order={5}>Getting started</Title>
            </Group>
            <Text c="dimmed" size="sm">
              Use the sidebar to curate courses, upload session media, build the knowledge base, configure
              prerequisites, inspect per-volunteer progress, and view aggregated analytics. Lookups
              (visibility, statuses, step types) are editable from the <strong>Lookups</strong> page — no
              deploy required. Charts adapt to light and dark themes automatically.
            </Text>
            <Group mt="sm">
              <Link to="/app/resources/courses" style={{ textDecoration: 'none' }}>
                <Button variant="light" leftSection={<IconBooks size={16} />}>
                  Open courses
                </Button>
              </Link>
              <Link to="/app/resources/lookups" style={{ textDecoration: 'none' }}>
                <Button variant="default" leftSection={<IconSettings size={16} />}>
                  Edit lookups
                </Button>
              </Link>
            </Group>
          </Card>
        </Stack>
      </FadeIn>
    </LayoutContainer>
  );
}
